(function () {
  'use strict';

  const { SingleProduct, ProductForm, HoverZoom, Lightbox, subEvents, PUB_SUB_EVENTS } = window.Universe;

  /**
   * ModalProduct
   *
   * Drop-in replacement for QuickAdd that renders the full main product
   * experience (u-main-product) inside a modal instead of the simplified
   * quick-add card. It reuses the SingleProduct engine for all block logic
   * (media carousel, variants, quantity, size chart, pickup, accordions, etc.)
   * and only owns the modal shell + variant-driven refetch.
   *
   * Content is fetched from the `product.modal` view template (`?view=modal`),
   * which renders u-main-product with the sticky add-to-cart disabled.
   *
   * NOTE: the Section Rendering API (`?sections=`) is unavailable in this theme,
   * so variant changes refetch the full `?view=modal` document and rebuild the
   * inner content (same strategy the legacy QuickAdd used).
   */
  class ModalProduct {
    constructor(card, container, options) {
      this.init(card, container, options);
    }

    init(card, container, options) {
      if (!card || !container) return;

      // `view` selects the alternate product template to fetch (`?view=...`).
      // Defaults to `modal`; subclasses (e.g. ReelModal) may pass their own.
      this.options = options || {};
      this.view = this.options.view || 'modal';

      this.DOM = {};
      this.DOM.card = card;
      this.DOM.container = container;
      this.isOpen = false;
      this.focusTrapActive = false;

      this.handleCartUpdate = (data) => {
        if (data.source === 'product-form' && this.DOM.modal && this.isOpen) {
          this.closeModal();
        }
      };

      this.createModal = (e) => {
        e.stopPropagation();
        e.preventDefault();

        this.openerCard = this.DOM.card;
        if (!this.openerCard.hasAttribute('tabindex')) {
          this.openerCard.setAttribute('tabindex', '-1');
          this.openerCardTabindexAdded = true;
        }

        const productUrl = this.DOM.card.dataset.handle;
        this.fetchURL(this.withParams(productUrl, { view: this.view }));
      };

      this.destroyModal = (e) => {
        if (e.target === e.currentTarget && this.DOM.modal) this.closeModal();
      };

      this.handleEscapeKey = (e) => {
        if (e.key !== 'Escape' || !this.isOpen) return;
        // Let an open media lightbox consume Escape first without closing the modal.
        if (document.querySelector('.js-product-modal[open]')) return;
        this.closeModal();
      };

      this.handleTabTrap = (e) => {
        if (!this.focusTrapActive || !this.DOM.modal) return;
        if (e.key !== 'Tab') return;
        const focusables = this.getFocusableElements(this.DOM.modal);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      this.onOptionValueChange = (payload) => {
        const option = payload.data.target;
        const variantURL = option.dataset.productUrl || option.dataset.url || this.DOM.card.dataset.handle;
        this.fetchURL(this.withParams(variantURL, { view: this.view }), true);
      };

      this.setup();
    }

    setup() {
      this.DOM.viewBtns = this.DOM.card.querySelectorAll('.js-view-card');
      this.DOM.viewBtns?.forEach((btn) => btn.addEventListener('click', this.createModal));
    }

    /** Safely add/override query params regardless of an existing query string. */
    withParams(url, params) {
      const parts = url.split('?');
      const base = parts[0];
      const search = new URLSearchParams(parts[1] || '');
      Object.keys(params).forEach((key) => search.set(key, params[key]));
      return base + '?' + search.toString();
    }

    fetchURL(url, reinit) {
      fetch(url)
        .then((response) => response.text())
        .then((html) => this.render(html, reinit))
        .catch((error) => {
          console.error('Error loading the product modal:', error);
        });
    }

    buildShell() {
      const shell = document.createElement('div');
      shell.className = 'modal-product js-modal-product' + this.shellModifier();

      shell.innerHTML =
        '<div class="modal-product__inner">' +
        '<button type="button" class="modal-product__close js-modal-product-close" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>' +
        '</button>' +
        this.extraSlots() +
        '<div class="modal-product__content js-modal-product-content"></div>' +
        '</div>';
      return shell;
    }

    /* -------- Subclass hooks (no-ops in the base modal) -------- */

    /** Extra class(es) appended to the shell root, e.g. a variant modifier. */
    shellModifier() {
      return '';
    }

    /** Extra persistent markup injected into the shell before `__content`. */
    extraSlots() {
      return '';
    }

    /** Runs once after the shell is mounted and opened. */
    onOpened() {}

    /** Runs at the start of closeModal(), before teardown. */
    beforeClose() {}

    /** Runs during destroy(), for subclass-owned cleanup. */
    onDestroy() {}

    render(html, reinit) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.js-product-template-section');
      if (!content) return;

      // Avoid duplicate structured data / element ids when injected into another page.
      content.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
      const duplicateId = content.querySelector('#u-main-product');
      if (duplicateId) duplicateId.removeAttribute('id');

      // Breadcrumbs don't belong in the modal context.
      content.querySelectorAll('.breadcrumb').forEach((node) => node.remove());

      if (reinit && this.DOM.modal) {
        this.teardownInstance();
        this.DOM.content.textContent = '';
        this.DOM.content.appendChild(content);
        this.applyColorScheme(content);
        this.revealScrollElements();
        this.bootInstance();
        return;
      }

      this.DOM.container.textContent = '';
      this.DOM.modal = this.buildShell();
      this.DOM.inner = this.DOM.modal.querySelector('.modal-product__inner');
      this.DOM.content = this.DOM.modal.querySelector('.js-modal-product-content');
      this.DOM.closeBtn = this.DOM.modal.querySelector('.js-modal-product-close');

      this.DOM.content.appendChild(content);
      this.applyColorScheme(content);
      this.revealScrollElements();
      this.DOM.container.appendChild(this.DOM.modal);
      document.body.classList.add('modal-open');

      this.DOM.modal.addEventListener('click', this.destroyModal);
      this.closeHandler = () => this.closeModal();
      this.DOM.closeBtn.addEventListener('click', this.closeHandler);
      document.addEventListener('keydown', this.handleEscapeKey);

      this.cartUpdateUnsub = subEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      this.optionUnsub = subEvents.subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onOptionValueChange);

      this.bootInstance();

      requestAnimationFrame(() => {
        this.DOM.modal.classList.add('modal-product--open');
      });
      this.isOpen = true;
      this.onOpened();
      this.enableModalA11y();
    }

    /** Mirror the injected section's color scheme onto the shell so tokens resolve. */
    applyColorScheme(content) {
      const wrap = content.querySelector('.js-single-product');
      if (!wrap || !this.DOM.inner) return;
      const scheme = Array.from(wrap.classList).find((cls) => cls.indexOf('color-') === 0);
      if (scheme) {
        this.DOM.inner.classList.forEach((cls) => {
          if (cls.indexOf('color-') === 0) this.DOM.inner.classList.remove(cls);
        });
        this.DOM.inner.classList.add(scheme);
      }
    }

    /**
     * The global scroll-reveal IntersectionObserver only watches elements that
     * existed at page load, so blocks injected into the modal never receive the
     * `is-visible` class and stay at opacity 0. Force-reveal them on inject, the
     * same way SingleProduct.swapProduct does for hot-swapped content.
     */
    revealScrollElements() {
      if (!this.DOM.content) return;
      this.DOM.content.querySelectorAll('[data-scroll]').forEach((el) => el.classList.add('is-visible'));
    }

    bootInstance() {
      this.singleProduct = new SingleProduct(this.DOM.content);

      // The main product engine drives variant changes through the Section
      // Rendering API and rewrites the address bar — neither works inside a
      // modal on another page. Neutralize both; ModalProduct owns variants.
      if (this.singleProduct) {
        this.singleProduct.variantUpdateUnsubscriber?.();
        this.singleProduct.variantUpdateUnsubscriber = undefined;
        this.singleProduct.stickyVariantUnsubscriber?.();
        this.singleProduct.stickyVariantUnsubscriber = undefined;
        this.singleProduct.updateUrl = 'false';
      }

      const form = this.DOM.content.querySelector('.js-product-form');
      if (form) this.form = new ProductForm(form);

      // Image zoom is normally wired by the product page controller, not by
      // SingleProduct — instantiate both mechanisms scoped to the modal so they
      // don't double-bind a product page's own media when opened there.
      if (HoverZoom) this.hoverZoom = new HoverZoom(this.DOM.content);
      if (Lightbox) {
        // Lightbox relocates the media modal to document.body on open; keep a
        // reference so we can remove it on teardown, and mark it so it stacks
        // above the product modal (which sits at z-200).
        this.lightboxModal = this.DOM.content.querySelector('.js-product-modal');
        if (this.lightboxModal) this.lightboxModal.classList.add('modal-product__lightbox');
        this.lightbox = new Lightbox(this.DOM.content);
      }
    }

    teardownInstance() {
      if (this.lightbox) {
        this.lightbox.destroy();
        this.lightbox = undefined;
      }
      if (this.lightboxModal) {
        // Remove the media modal even if Lightbox moved it out to document.body.
        this.lightboxModal.remove();
        this.lightboxModal = undefined;
      }
      if (this.hoverZoom) {
        this.hoverZoom.destroy();
        this.hoverZoom = undefined;
      }
      if (this.form) {
        this.form.destroy();
        this.form = undefined;
      }
      if (this.singleProduct) {
        this.singleProduct.destroy();
        this.singleProduct = undefined;
      }
    }

    closeModal() {
      if (!this.DOM.modal) return;

      this.beforeClose();
      this.disableModalA11y();

      this.cartUpdateUnsub?.();
      this.cartUpdateUnsub = undefined;
      this.optionUnsub?.();
      this.optionUnsub = undefined;

      this.DOM.modal.classList.remove('modal-product--open');
      this.DOM.modal.removeEventListener('click', this.destroyModal);
      this.DOM.closeBtn?.removeEventListener('click', this.closeHandler);
      document.removeEventListener('keydown', this.handleEscapeKey);

      this.teardownInstance();

      const cardToFocus = this.openerCard;
      const tabindexWasAdded = this.openerCardTabindexAdded;
      this.isOpen = false;

      const animationDuration = 500;
      setTimeout(() => {
        document.body.classList.remove('modal-open');
        if (this.DOM.container) this.DOM.container.textContent = '';
        this.DOM.modal = undefined;
        this.DOM.inner = undefined;
        this.DOM.content = undefined;
        this.DOM.closeBtn = undefined;

        if (cardToFocus && typeof cardToFocus.focus === 'function') {
          cardToFocus.focus();
          if (tabindexWasAdded) cardToFocus.removeAttribute('tabindex');
        }
      }, animationDuration);
    }

    destroy() {
      if (!this.DOM) return;
      this.teardownInstance();
      this.onDestroy();
      this.DOM.viewBtns?.forEach((btn) => btn.removeEventListener('click', this.createModal));
      this.cartUpdateUnsub?.();
      this.cartUpdateUnsub = undefined;
      this.optionUnsub?.();
      this.optionUnsub = undefined;
      document.removeEventListener('keydown', this.handleEscapeKey);
      document.removeEventListener('keydown', this.handleTabTrap);
    }

    getFocusableElements(root) {
      if (!root) return [];
      const selectors = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]:not([tabindex="-1"])',
      ];
      return Array.from(root.querySelectorAll(selectors.join(','))).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
    }

    focusFirstInModal() {
      const focusables = this.getFocusableElements(this.DOM.modal);
      const target = this.DOM.closeBtn || focusables[0];
      if (target && typeof target.focus === 'function') target.focus();
    }

    setBackgroundInert(enable) {
      // Inert every top-level node EXCEPT the one containing the modal. Using
      // `contains` (rather than a strict `!== container` check) keeps this
      // correct even if the mount host is ever nested below <body> instead of
      // being a direct child.
      const bodyChildren = Array.from(document.body.children);
      this.backgroundNodes = bodyChildren.filter((node) => !node.contains(this.DOM.modal));

      const supportsInert = 'inert' in HTMLElement.prototype;
      this.backgroundNodes.forEach((node) => {
        if (enable) {
          if (supportsInert) node.inert = true;
          else node.setAttribute('aria-hidden', 'true');
        } else {
          if (supportsInert) node.inert = false;
          else node.removeAttribute('aria-hidden');
        }
      });
    }

    enableModalA11y() {
      this.setBackgroundInert(true);
      this.focusTrapActive = true;
      document.addEventListener('keydown', this.handleTabTrap);
      setTimeout(() => this.focusFirstInModal(), 500);
    }

    disableModalA11y() {
      this.setBackgroundInert(false);
      this.focusTrapActive = false;
      document.removeEventListener('keydown', this.handleTabTrap);
    }
  }

  window.Universe = window.Universe || {};
  window.Universe.ModalProduct = ModalProduct;
})();
